import { MoreHorizontal, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { CLIENT_DATA } from '../data/clients';
import { CONNECTIONS } from '../data/connections';
import type { Connection } from '../types';

interface ConnectionsTableProps {
	onAddNew: () => void;
	onView: (conn: Connection) => void;
	onEdit: (conn: Connection) => void;
	onTest: (conn: Connection) => void;
}

const typeBadgeVariant: Record<string, 'default' | 'secondary' | 'outline'> = {
	Email: 'default',
	Push: 'secondary',
	Sms: 'outline',
};

const statusBadgeVariant: Record<
	string,
	'default' | 'secondary' | 'destructive' | 'outline'
> = {
	Active: 'default',
	Inactive: 'secondary',
	Error: 'destructive',
};

export function ConnectionsTable({
	onAddNew,
	onView,
	onEdit,
	onTest,
}: ConnectionsTableProps) {
	const [search, setSearch] = useState('');
	const [showFilters, setShowFilters] = useState(false);
	const [filterStatus, setFilterStatus] = useState('');
	const [filterClient, setFilterClient] = useState('');
	const [filterType, setFilterType] = useState('');

	const rows = CONNECTIONS.filter((c) => {
		const matchSearch =
			search === '' ||
			c.provider.toLowerCase().includes(search.toLowerCase()) ||
			c.label.toLowerCase().includes(search.toLowerCase());
		const matchStatus = filterStatus === '' || c.status === filterStatus;
		const matchClient = filterClient === '' || c.clientName === filterClient;
		const matchType = filterType === '' || c.type === filterType;
		return matchSearch && matchStatus && matchClient && matchType;
	});

	const clearFilters = () => {
		setFilterStatus('');
		setFilterClient('');
		setFilterType('');
	};

	const hasFilters = filterStatus || filterClient || filterType;

	return (
		<div className="space-y-4">
			{/* Warning banner for SES */}
			<div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
				<span className="text-amber-600">⚠</span>
				<span>
					Warning: AWS SES (Emergency) is in Error state and is part of your
					active Email Fallback Chain. Sends may fail at position 3.
				</span>
			</div>

			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search by Provider Label"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="h-9 w-56 pl-8 text-sm"
						/>
					</div>
					<Button
						variant={showFilters ? 'secondary' : 'outline'}
						size="sm"
						className="gap-1.5"
						onClick={() => setShowFilters((f) => !f)}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						{showFilters ? 'Hide Filters' : 'Show Filters'}
					</Button>
				</div>
				<Button size="sm" onClick={onAddNew}>
					<Plus className="mr-1 h-3.5 w-3.5" />+ New Connection
				</Button>
			</div>

			{showFilters && (
				<div className="flex items-end gap-3 rounded-lg border bg-muted/30 p-3">
					<div className="flex-1">
						<span className="mb-1 block text-xs text-muted-foreground">
							Status
						</span>
						<Select value={filterStatus} onValueChange={setFilterStatus}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Active">Active</SelectItem>
								<SelectItem value="Inactive">Inactive</SelectItem>
								<SelectItem value="Error">Error</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="flex-1">
						<span className="mb-1 block text-xs text-muted-foreground">
							Client Name
						</span>
						<Select value={filterClient} onValueChange={setFilterClient}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select Client" />
							</SelectTrigger>
							<SelectContent>
								{CLIENT_DATA.map((c) => (
									<SelectItem key={c.id} value={c.clientId}>
										{c.clientId}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="flex-1">
						<span className="mb-1 block text-xs text-muted-foreground">
							Connection Type
						</span>
						<Select value={filterType} onValueChange={setFilterType}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select Type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Email">Email</SelectItem>
								<SelectItem value="Push">Push</SelectItem>
								<SelectItem value="Sms">SMS</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{hasFilters && (
						<Button
							variant="outline"
							size="sm"
							className="h-9"
							onClick={clearFilters}
						>
							Clear
						</Button>
					)}
				</div>
			)}

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-12">ID</TableHead>
						<TableHead>Provider</TableHead>
						<TableHead>Label</TableHead>
						<TableHead>Type</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Region</TableHead>
						<TableHead>Created At</TableHead>
						<TableHead>Client Name</TableHead>
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((c) => (
						<TableRow
							key={c.id}
							className="cursor-pointer"
							onClick={() => onView(c)}
						>
							<TableCell className="text-xs text-muted-foreground">
								{c.id}
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									<span className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[10px] font-bold">
										{c.logo}
									</span>
									<div>
										<div className="text-sm font-medium">{c.provider}</div>
										<div className="text-xs text-muted-foreground">
											{c.cred}
										</div>
									</div>
								</div>
							</TableCell>
							<TableCell>{c.label}</TableCell>
							<TableCell>
								<Badge variant={typeBadgeVariant[c.type] || 'outline'}>
									{c.type}
								</Badge>
							</TableCell>
							<TableCell>
								<Badge variant={statusBadgeVariant[c.status] || 'outline'}>
									{c.status}
								</Badge>
							</TableCell>
							<TableCell className="text-muted-foreground">
								{c.region}
							</TableCell>
							<TableCell className="text-muted-foreground">
								{c.created}
							</TableCell>
							<TableCell className="text-muted-foreground">
								{c.clientName || '—'}
							</TableCell>
							<TableCell>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={(e) => e.stopPropagation()}
										>
											<MoreHorizontal className="h-3.5 w-3.5" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onView(c);
											}}
										>
											View details
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onEdit(c);
											}}
										>
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onTest(c);
											}}
										>
											Test connection
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<div className="flex items-center justify-between text-xs text-muted-foreground">
				<span>
					Showing 1–{rows.length} of {rows.length}
				</span>
				<div className="flex items-center gap-1">
					<span>Page 1 of 1</span>
					<Button variant="ghost" size="icon" className="h-6 w-6" disabled>
						›
					</Button>
				</div>
			</div>
		</div>
	);
}
